import { useState, useEffect, useRef } from 'react';
import { getDrawDateValue, parseDrawDate } from '../utils/dateFormat';

export function useCountdown(targetDate, options = {}) {
  const {
    onComplete = null,
    interval = 1000,
    enableAnimation = true
  } = options;

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    total: 0
  });

  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const completedRef = useRef(false);
  const targetValue = getDrawDateValue(targetDate);
  const targetOffset = targetDate && typeof targetDate === 'object' && !(targetDate instanceof Date)
    ? targetDate.utc_offset ?? targetDate.utcOffset ?? null
    : null;

  // Protect against unstable onComplete function reference changes restarting the effect
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!targetValue) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });
      setIsComplete(false);
      completedRef.current = false;
      return;
    }

    setIsComplete(false);
    completedRef.current = false;

    const calculateTimeLeft = () => {
      const now = new Date();
      const target = parseDrawDate(targetOffset != null
        ? { draw_date: targetValue, utc_offset: targetOffset }
        : targetValue);
      if (!target) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
      }
      const difference = target - now;

      if (difference <= 0) {
        if (!completedRef.current) {
          completedRef.current = true;
          setIsComplete(true);
          onCompleteRef.current?.();
        }
        return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
      }

      if (completedRef.current) {
        completedRef.current = false;
        setIsComplete(false);
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        total: difference
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();

      if (enableAnimation) {
        setShouldAnimate(true);
        setTimeout(() => setShouldAnimate(false), 300);
      }

      setTimeLeft(newTimeLeft);
    }, interval);

    return () => clearInterval(timer);
  }, [targetValue, targetOffset, interval, enableAnimation]);

  return {
    timeLeft: { ...timeLeft, isComplete },
    shouldAnimate,
    isComplete,
    formattedTime: formatTime(timeLeft),
    compactTime: formatCompactTime(timeLeft)
  };
}

function formatTime(timeLeft) {
  const parts = [];

  parts.push(`${timeLeft.days}d`);

  parts.push(
    `${String(timeLeft.hours).padStart(2, '0')}:${String(timeLeft.minutes).padStart(2, '0')}:${String(timeLeft.seconds).padStart(2, '0')}`
  );

  return parts.join(' ');
}

function formatCompactTime(timeLeft) {
  return `${timeLeft.days}d ${String(timeLeft.hours).padStart(2, '0')}:${String(timeLeft.minutes).padStart(2, '0')}:${String(timeLeft.seconds).padStart(2, '0')}`;
}
